// Badge de Notificação do Quiz
// Componente que exibe um badge pulsante no avatar do membro quando há quiz disponível

import React from 'react';
import { logger } from '../../utils/logger';

interface QuizNotificationBadgeProps {
  hasAvailableQuiz: boolean;
  quizProgress?: {
    completed: number;
    total: number;
  };
  className?: string;
  onClick?: () => void;
}

export const QuizNotificationBadge: React.FC<QuizNotificationBadgeProps> = ({
  hasAvailableQuiz,
  quizProgress,
  className = '',
  onClick
}) => {
  // Sempre mostra o badge se há quiz disponível OU se há progresso
  if (!hasAvailableQuiz && (!quizProgress || (quizProgress.completed === 0 && quizProgress.total === 0))) {
    return null;
  }

  const isCompleted = quizProgress && quizProgress.completed >= quizProgress.total && quizProgress.total > 0;
  const hasPartialProgress = quizProgress && quizProgress.completed > 0 && quizProgress.completed < quizProgress.total;

  return (
    <div className={`${className}`}>
      {/* Badge principal - posicionado no topo e à direita do avatar */}
      <div
        className={`
          absolute -top-2 -right-2 min-w-[28px] h-7 
          rounded-full flex items-center justify-center text-sm font-bold text-white
          cursor-pointer transition-all duration-300 z-20 shadow-lg border-2 border-white
          ${isCompleted 
            ? 'bg-green-500 hover:bg-green-600' 
            : hasPartialProgress 
              ? 'bg-yellow-500 hover:bg-yellow-600' 
              : 'bg-blue-500 hover:bg-blue-600 animate-pulse'
          }
          ${onClick ? 'hover:scale-110' : ''}
        `}
        onClick={onClick}
        title={
          isCompleted 
            ? 'Quiz do dia concluído! Clique para ver o resumo'
            : hasPartialProgress
              ? `Quiz em andamento: ${quizProgress?.completed}/${quizProgress?.total}`
              : 'Novo quiz disponível! Clique para começar'
        }
      >
        {isCompleted ? (
          // Ícone de check para quiz concluído
          <svg 
            className="w-4 h-4" 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path 
              fillRule="evenodd" 
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
              clipRule="evenodd" 
            />
          </svg>
        ) : hasPartialProgress ? (
          // Mostra progresso parcial
          <span className="text-xs font-bold">
            {quizProgress?.completed}
          </span>
        ) : (
          // Ícone de interrogação para quiz disponível
          <svg 
            className="w-4 h-4" 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path 
              fillRule="evenodd" 
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" 
              clipRule="evenodd" 
            />
          </svg>
        )}
      </div>

      {/* Efeito de pulso para quiz disponível - ajustado para nova posição */}
      {hasAvailableQuiz && !isCompleted && (
        <div className="absolute -top-2 -right-2 w-7 h-7 bg-blue-400 rounded-full animate-ping opacity-75"></div>
      )}

      {/* Efeito de brilho para quiz concluído - ajustado para nova posição */}
      {isCompleted && (
        <div className="absolute -top-2 -right-2 w-7 h-7 bg-green-400 rounded-full animate-pulse opacity-50"></div>
      )}
    </div>
  );
};

// Componente wrapper para facilitar integração
interface QuizBadgeWrapperProps {
  memberId: string;
  children: React.ReactNode;
  onQuizClick?: () => void;
  className?: string;
}

export const QuizBadgeWrapper: React.FC<QuizBadgeWrapperProps> = ({
  memberId,
  children,
  onQuizClick,
  className = ''
}) => {
  const [hasAvailableQuiz, setHasAvailableQuiz] = React.useState(false);
  const [quizProgress, setQuizProgress] = React.useState<{completed: number; total: number} | undefined>();

  React.useEffect(() => {
    // Importação dinâmica para evitar problemas de dependência circular
    const checkQuizStatus = async () => {
      try {
        const { QuizSystem } = await import('../../utils/QuizSystem');
        const quizSystem = QuizSystem.getInstance();
        
        // Usar getQuizStatus que verifica no banco de dados
        const status = await quizSystem.getQuizStatus(memberId);
        
        // Força a exibição do badge se há quiz disponível ou se foi completado
        setHasAvailableQuiz(status.canTakeQuiz || status.hasCompletedToday);
        setQuizProgress({
          completed: status.questionsAnswered,
          total: status.totalQuestions
        });
        
        // Debug log para verificar o status
        logger.debug('Status do quiz:', { hasCompletedToday: status.hasCompletedToday, canTakeQuiz: status.canTakeQuiz, questionsAnswered: status.questionsAnswered, shouldShow: status.canTakeQuiz || status.hasCompletedToday });
      } catch (error) {
        console.error('❌ [QuizBadge] Erro ao verificar status do quiz:', error);
      }
    };

    checkQuizStatus();

    // Verifica status a cada 30 segundos
    const interval = setInterval(checkQuizStatus, 30000);
    
    return () => clearInterval(interval);
  }, [memberId]);

  return (
    <div className={`relative ${className}`}>
      {children}
      <QuizNotificationBadge
        hasAvailableQuiz={hasAvailableQuiz}
        quizProgress={quizProgress}
        onClick={onQuizClick}
      />
    </div>
  );
};

export default QuizNotificationBadge;